// components/DependentOnTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  Button,
} from "@mui/material";
import TaskSearch from "./TaskSearch";
import TaskList from "./TaskList";

interface DependentOnTabProps {
  selectedProject: any;
  dependencies: any[];
  onUpdateDependencies: (dependencies: any[]) => void;
  currentProjectId: string;
}

const DependentOnTab: React.FC<DependentOnTabProps> = ({
  selectedProject,
  dependencies,
  onUpdateDependencies,
  currentProjectId,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const [memberType, setMemberType] = useState("Observer");

  const handleMemberTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMemberType(event.target.value);
  };

  const handleSearch = (query: string) => {};

  const handleAddTask = (task: any) => {
    // Check if task is already added
    const isAlreadyAdded = dependencies.some(
      (existingTask) => existingTask._id === task._id
    );

    if (!isAlreadyAdded) {
      const newTask = {
        ...task,
        projectKey: selectedProject?.key || selectedProject?._id || "",
        projectId: task.project?._id || selectedProject?._id,
        depType: "Waiting", // Changed from type to depType
        status: "Waiting On",
      };

      const updatedDependencies = [...dependencies, newTask];
      onUpdateDependencies(updatedDependencies);
    }
  };

  const handleUpdateTask = (taskId: string, updates: any) => {
    const updatedDependencies = dependencies.map((task) =>
      task._id === taskId ? { ...task, ...updates } : task
    );
    onUpdateDependencies(updatedDependencies);
  };

  const handleDeleteTask = (taskId: string) => {
    const updatedDependencies = dependencies.filter(
      (task) => task._id !== taskId
    );
    onUpdateDependencies(updatedDependencies);
  };

  if (!selectedProject) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#666",
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {t("Select a project to view dependencies")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Project Name and Options with Approve Button */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
          {selectedProject.name}
        </Typography>

        {/* Member Type Selection and Approve Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <RadioGroup
            row
            value={memberType}
            onChange={handleMemberTypeChange}
            sx={{ gap: 3 }}
          >
            <FormControlLabel
              value="Observer"
              control={<Radio size="small" color="success" />}
              label={t("Observer")}
            />
            <FormControlLabel
              value="Active Member"
              control={<Radio size="small" />}
              label={t("Active Member")}
            />
          </RadioGroup>

          {/* Approve Button */}
          <Button
            variant="contained"
            disabled // Keep it static/non-functional as requested
            sx={{
              backgroundColor: "#6366f1",
              "&:hover": {
                backgroundColor: "#5856eb",
              },
              "&.Mui-disabled": {
                backgroundColor: "#6366f1",
                color: "white",
                opacity: 0.7,
              },
            }}
          >
            {t("Approve")}
          </Button>
        </Box>
      </Box>

      {/* Search */}
      <TaskSearch
        onSearch={handleSearch}
        onAddTask={handleAddTask}
        placeholder={t("Search by task number or task name")}
        selectedProject={selectedProject}
      />

      <Divider />

      {/* Task List */}
      <TaskList
        tasks={dependencies}
        selectedProject={selectedProject}
        onUpdateTask={handleUpdateTask}
        onDeleteTask={handleDeleteTask}
      />
    </Box>
  );
};

export default DependentOnTab;
